import { Connection, PublicKey, Transaction, TransactionInstruction } from '@solana/web3.js';
import { getCurrentRpcEndpoint, SOLANA_CONFIG } from '../config/security';
import { secureStorage, sanitizeErrorMessage, sanitizeInput } from '../utils/security';
import ReputationService from './ReputationService';
import DynamicGovernanceConfig from './DynamicGovernanceConfig';

/**
 * DAO Governance Service for Content Moderation
 * Handles content flagging, community voting, and governance decisions
 */
export class DAOGovernanceService {
  constructor(connection, wallet) {
    this.connection = connection || new Connection(getCurrentRpcEndpoint());
    this.wallet = wallet;
    this.reputationService = new ReputationService(connection, wallet);
    this.znoozinuTokenAddress = new PublicKey('5cLKdANQXzUPMXgpba19Zwprhmi8yQW6R5D66oTQZ9ZN');
    this.dynamicConfig = DynamicGovernanceConfig;
    
    // Dynamic voting parameters - now loaded from DynamicGovernanceConfig
    this.refreshVotingConfig();

    // Content moderation categories
    this.MODERATION_CATEGORIES = {
      SPAM: { severity: 'LOW', autoAction: 'HIDE', description: 'Spam or repetitive content' },
      HARASSMENT: { severity: 'MEDIUM', autoAction: 'REMOVE', description: 'Harassment or bullying' },
      HATE_SPEECH: { severity: 'HIGH', autoAction: 'REMOVE', description: 'Hate speech or discrimination' },
      MISINFORMATION: { severity: 'MEDIUM', autoAction: 'FLAG', description: 'False or misleading information' },
      ADULT_CONTENT: { severity: 'MEDIUM', autoAction: 'HIDE', description: 'Adult or explicit content' },
      COPYRIGHT: { severity: 'HIGH', autoAction: 'REMOVE', description: 'Copyright infringement' },
      SCAM: { severity: 'HIGH', autoAction: 'REMOVE', description: 'Scam or fraudulent content' },
      OTHER: { severity: 'LOW', autoAction: 'REVIEW', description: 'Other policy violation' }
    };

    // Proposal types
    this.PROPOSAL_TYPES = {
      CONTENT_MODERATION: 'Content moderation decision',
      POLICY_CHANGE: 'Platform policy change',
      PARAMETER_UPDATE: 'System parameter update',
      USER_SANCTIONS: 'User sanctions or penalties',
      FEATURE_REQUEST: 'New feature implementation',
      TREASURY_ALLOCATION: 'Treasury fund allocation'
    };
  }

  /**
   * Refresh voting configuration from dynamic config service
   */
  refreshVotingConfig() {
    this.VOTING_CONFIG = this.dynamicConfig.getCurrentVotingConfig();
    console.log('🔄 DAO: Updated voting config for', this.VOTING_CONFIG.phase, 'phase');
    console.log('📊 Current thresholds:', {
      vote: this.VOTING_CONFIG.MIN_STAKE_TO_VOTE,
      propose: this.VOTING_CONFIG.MIN_STAKE_TO_PROPOSE,
      quorum: this.VOTING_CONFIG.QUORUM_THRESHOLD,
      cap: this.VOTING_CONFIG.VOTING_POWER_CAP
    });
  }

  /**
   * Flag content for community review
   */
  async flagContent(contentId, category, reason, evidence = []) {
    try {
      if (!this.wallet) {
        throw new Error('Wallet not connected');
      }

      // Validate category
      if (!this.MODERATION_CATEGORIES[category]) {
        throw new Error('Invalid moderation category');
      }

      // Check if user can flag content
      const canFlag = await this.reputationService.canPerformAction(
        this.wallet.publicKey.toString(),
        'FLAG_CONTENT'
      );

      if (!canFlag.allowed) {
        throw new Error(`Cannot flag content: ${canFlag.reason}`);
      }

      // Sanitize inputs
      const sanitizedReason = sanitizeInput(reason);
      const sanitizedEvidence = evidence.map(item => sanitizeInput(item));

      // Create flag data
      const flagData = {
        contentId,
        category,
        reason: sanitizedReason,
        evidence: sanitizedEvidence,
        flagger: this.wallet.publicKey.toString(),
        timestamp: Date.now(),
        severity: this.MODERATION_CATEGORIES[category].severity,
        autoAction: this.MODERATION_CATEGORIES[category].autoAction
      };

      // Create blockchain transaction
      const transaction = new Transaction();
      
      const flagInstruction = new TransactionInstruction({
        keys: [
          { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true }
        ],
        programId: new PublicKey(SOLANA_CONFIG.PROGRAM_IDS.CHIRPX),
        data: Buffer.from(`ChirpX Flag: ${JSON.stringify(flagData)}`, 'utf8')
      });

      transaction.add(flagInstruction);

      // Get recent blockhash and send
      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;

      const signedTransaction = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      
      await this.connection.confirmTransaction(signature, 'confirmed');

      // Record action for rate limiting
      await this.reputationService.recordAction(
        this.wallet.publicKey.toString(),
        'FLAG_CONTENT'
      );

      // Auto-create DAO proposal for high severity flags
      if (flagData.severity === 'HIGH') {
        await this.createModerationProposal(contentId, flagData);
      }

      return {
        success: true,
        flagId: signature,
        signature,
        flagData,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`
      };
    } catch (error) {
      console.error('Error flagging content:', sanitizeErrorMessage(error));
      return {
        success: false,
        error: sanitizeErrorMessage(error)
      };
    }
  }

  /**
   * Create a DAO proposal for content moderation
   */
  async createModerationProposal(contentId, flagData) {
    try {
      const proposalData = {
        type: 'CONTENT_MODERATION',
        contentId,
        title: `Moderate ${flagData.category} Content`,
        description: `Community vote on flagged content: ${flagData.reason}`,
        flagData,
        proposer: this.wallet.publicKey.toString(),
        createdAt: Date.now(),
        votingEndsAt: Date.now() + this.VOTING_CONFIG.VOTING_PERIOD,
        options: [
          { id: 'APPROVE_FLAG', label: 'Remove Content', description: 'Approve flag and remove content' },
          { id: 'REJECT_FLAG', label: 'Keep Content', description: 'Reject flag and keep content' },
          { id: 'ESCALATE', label: 'Escalate to Admin', description: 'Escalate to human moderator' }
        ],
        votes: {},
        totalVotes: 0,
        totalVotingPower: 0,
        status: 'ACTIVE'
      };

      // Store proposal
      const proposalId = `proposal_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      secureStorage.setItem(`dao_proposal_${proposalId}`, proposalData);

      // Create blockchain record
      const transaction = new Transaction();
      
      const proposalInstruction = new TransactionInstruction({
        keys: [
          { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true }
        ],
        programId: new PublicKey(SOLANA_CONFIG.PROGRAM_IDS.CHIRPX),
        data: Buffer.from(`ChirpX DAO Proposal: ${JSON.stringify({
          proposalId,
          type: proposalData.type,
          contentId,
          proposer: proposalData.proposer,
          timestamp: proposalData.createdAt
        })}`, 'utf8')
      });

      transaction.add(proposalInstruction);

      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;

      const signedTransaction = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      
      await this.connection.confirmTransaction(signature, 'confirmed');

      return {
        success: true,
        proposalId,
        signature,
        proposalData
      };
    } catch (error) {
      console.error('Error creating proposal:', sanitizeErrorMessage(error));
      return {
        success: false,
        error: sanitizeErrorMessage(error)
      };
    }
  }

  /**
   * Vote on a DAO proposal
   */
  async voteOnProposal(proposalId, optionId, votingPower = null) {
    try {
      if (!this.wallet) {
        throw new Error('Wallet not connected');
      }

      // Check voting eligibility
      const canVote = await this.reputationService.canPerformAction(
        this.wallet.publicKey.toString(),
        'DAO_VOTE'
      );

      if (!canVote.allowed) {
        throw new Error(`Cannot vote: ${canVote.reason}`);
      }

      // Get proposal
      const proposal = secureStorage.getItem(`dao_proposal_${proposalId}`);
      if (!proposal) {
        throw new Error('Proposal not found');
      }

      // Check if voting is still active
      if (Date.now() > proposal.votingEndsAt) {
        throw new Error('Voting period has ended');
      }

      // Check if user already voted
      const voterAddress = this.wallet.publicKey.toString();
      if (proposal.votes[voterAddress]) {
        throw new Error('User has already voted on this proposal');
      }

      // Calculate voting power based on token holdings
      const tokenBalance = await this.getTokenBalance(voterAddress);
      this.refreshVotingConfig();
      const actualVotingPower = Math.min(
        votingPower || tokenBalance,
        this.VOTING_CONFIG.VOTING_POWER_CAP,
        tokenBalance
      );

      if (actualVotingPower < this.VOTING_CONFIG.MIN_STAKE_TO_VOTE) {
        throw new Error(`Insufficient tokens to vote. Need at least ${this.VOTING_CONFIG.MIN_STAKE_TO_VOTE}`);
      }

      // Record vote
      const voteData = {
        voter: voterAddress,
        optionId,
        votingPower: actualVotingPower,
        timestamp: Date.now(),
        reputation: await this.reputationService.getUserReputation(voterAddress)
      };

      // Update proposal
      proposal.votes[voterAddress] = voteData;
      proposal.totalVotes += 1;
      proposal.totalVotingPower += actualVotingPower;

      // Update vote counts by option
      if (!proposal.voteCounts) proposal.voteCounts = {};
      proposal.voteCounts[optionId] = (proposal.voteCounts[optionId] || 0) + actualVotingPower;

      // Save updated proposal
      secureStorage.setItem(`dao_proposal_${proposalId}`, proposal);

      // Create blockchain record
      const transaction = new Transaction();
      
      const voteInstruction = new TransactionInstruction({
        keys: [
          { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true }
        ],
        programId: new PublicKey(SOLANA_CONFIG.PROGRAM_IDS.CHIRPX),
        data: Buffer.from(`ChirpX DAO Vote: ${JSON.stringify({
          proposalId,
          voter: voterAddress,
          optionId,
          votingPower: actualVotingPower,
          timestamp: Date.now()
        })}`, 'utf8')
      });

      transaction.add(voteInstruction);

      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;

      const signedTransaction = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTransaction.serialize());
      
      await this.connection.confirmTransaction(signature, 'confirmed');

      // Record action for reputation
      await this.reputationService.recordAction(voterAddress, 'DAO_VOTE');

      // Check if proposal should be executed
      await this.checkProposalExecution(proposalId);

      return {
        success: true,
        voteId: signature,
        signature,
        voteData,
        proposalStatus: proposal.status,
        explorerUrl: `https://explorer.solana.com/tx/${signature}?cluster=devnet`
      };
    } catch (error) {
      console.error('Error voting on proposal:', sanitizeErrorMessage(error));
      return {
        success: false,
        error: sanitizeErrorMessage(error)
      };
    }
  }

  /**
   * Check if proposal should be executed based on votes
   */
  async checkProposalExecution(proposalId) {
    try {
      const proposal = secureStorage.getItem(`dao_proposal_${proposalId}`);
      if (!proposal || proposal.status !== 'ACTIVE') return;

      // Check if voting period ended or quorum reached
      const votingEnded = Date.now() > proposal.votingEndsAt;
      const quorumReached = proposal.totalVotingPower >= this.VOTING_CONFIG.QUORUM_THRESHOLD;

      if (votingEnded || quorumReached) {
        // Calculate results
        const results = this.calculateVotingResults(proposal);
        
        // Update proposal status
        proposal.status = 'COMPLETED';
        proposal.results = results;
        proposal.executedAt = Date.now();

        // Execute the decision
        if (results.winningOption && results.approval >= this.VOTING_CONFIG.APPROVAL_THRESHOLD) {
          await this.executeProposalDecision(proposal, results);
        }

        // Save updated proposal
        secureStorage.setItem(`dao_proposal_${proposalId}`, proposal);
      }
    } catch (error) {
      console.error('Error checking proposal execution:', sanitizeErrorMessage(error));
    }
  }

  /**
   * Calculate voting results
   */
  calculateVotingResults(proposal) {
    const voteCounts = proposal.voteCounts || {};
    const totalVotes = proposal.totalVotingPower;
    
    let winningOption = null;
    let maxVotes = 0;
    
    const results = {};
    
    for (const [optionId, votes] of Object.entries(voteCounts)) {
      const percentage = totalVotes > 0 ? votes / totalVotes : 0;
      results[optionId] = {
        votes,
        percentage,
        label: proposal.options.find(opt => opt.id === optionId)?.label || optionId
      };
      
      if (votes > maxVotes) {
        maxVotes = votes;
        winningOption = optionId;
      }
    }
    
    return {
      results,
      winningOption,
      approval: totalVotes > 0 ? maxVotes / totalVotes : 0,
      totalVotes,
      quorumReached: totalVotes >= this.VOTING_CONFIG.QUORUM_THRESHOLD
    };
  }

  /**
   * Execute proposal decision
   */
  async executeProposalDecision(proposal, results) {
    try {
      if (proposal.type === 'CONTENT_MODERATION') {
        switch (results.winningOption) {
          case 'APPROVE_FLAG':
            await this.executeContentRemoval(proposal.contentId);
            break;
          case 'REJECT_FLAG':
            await this.executeContentApproval(proposal.contentId);
            break;
          case 'ESCALATE':
            await this.escalateToAdmin(proposal.contentId, proposal.flagData);
            break;
        }
      }
    } catch (error) {
      console.error('Error executing proposal decision:', sanitizeErrorMessage(error));
    }
  }

  /**
   * Execute content removal
   */
  async executeContentRemoval(contentId) {
    // Mark content as removed
    secureStorage.setItem(`content_status_${contentId}`, {
      status: 'REMOVED',
      reason: 'DAO_VOTE',
      timestamp: Date.now()
    });
  }

  /**
   * Execute content approval
   */
  async executeContentApproval(contentId) {
    // Mark content as approved
    secureStorage.setItem(`content_status_${contentId}`, {
      status: 'APPROVED',
      reason: 'DAO_VOTE',
      timestamp: Date.now()
    });
  }

  /**
   * Escalate to admin review
   */
  async escalateToAdmin(contentId, flagData) {
    // Add to admin review queue
    const adminQueue = secureStorage.getItem('admin_review_queue') || [];
    adminQueue.push({
      contentId,
      flagData,
      escalatedAt: Date.now(),
      status: 'PENDING_ADMIN_REVIEW'
    });
    secureStorage.setItem('admin_review_queue', adminQueue);
  }

  /**
   * Get user's token balance
   */
  async getTokenBalance(walletAddress) {
    try {
      const tokenAccounts = await this.connection.getTokenAccountsByOwner(
        new PublicKey(walletAddress),
        { mint: this.znoozinuTokenAddress }
      );

      let totalBalance = 0;
      for (const account of tokenAccounts.value) {
        const accountInfo = await this.connection.getTokenAccountBalance(account.pubkey);
        totalBalance += parseInt(accountInfo.value.amount) / Math.pow(10, accountInfo.value.decimals);
      }

      return totalBalance;
    } catch (error) {
      console.error('Error getting token balance:', sanitizeErrorMessage(error));
      return 0;
    }
  }

  /**
   * Get active proposals
   */
  getActiveProposals() {
    try {
      const proposals = [];
      const keys = Object.keys(localStorage);
      const SECURE_PREFIX = 'znoospace_secure_';
      const LEGACY_PREFIX = 'chirpx_secure_';
      
      for (const key of keys) {
        const isNew = key.startsWith(`${SECURE_PREFIX}dao_proposal_`);
        const isLegacy = key.startsWith(`${LEGACY_PREFIX}dao_proposal_`);
        
        if (isNew || isLegacy) {
          const prefix = isNew ? SECURE_PREFIX : LEGACY_PREFIX;
          const proposal = secureStorage.getItem(key.replace(prefix, ''));
          if (proposal && proposal.status === 'ACTIVE') {
            proposals.push({ id: key.replace(`${prefix}dao_proposal_`, ''), ...proposal });
          }
        }
      }
      
      return proposals.sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      console.error('Error getting active proposals:', sanitizeErrorMessage(error));
      return [];
    }
  }

  /**
   * Get proposal by ID
   */
  getProposal(proposalId) {
    return secureStorage.getItem(`dao_proposal_${proposalId}`);
  }

  /**
   * Get content moderation status
   */
  getContentStatus(contentId) {
    return secureStorage.getItem(`content_status_${contentId}`);
  }
}

export default DAOGovernanceService;
